import { action } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import { A, pipe } from "@mobily/ts-belt"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import {
  filterCourseDateDefault,
  isCourseDateCustom,
  isOnCourseDateDefault,
} from "cc-frontend/utils/filter-dates/course"
import fade from "ember-animated/transitions/fade"
import styles from "./courses-editor.module.css"

interface Args {
  courses: Array<Course>
  planbook: Planbook
  rotationCalendar: RotationCalendar
  hideCopyCoursesButton: boolean
}

export default class CoursesEditorComponent extends Component<Args> {
  readonly styles = styles

  @service("ember-store") declare store: EmberStore
  @service declare session
  @service declare rpc: Rpc
  @service declare dialog
  @service declare finder
  @service declare modalManager: ModalManager

  readonly fadeTransition = fade

  @tracked isCreatingCourse = false
  @tracked creatingCourseId: null | string = null
  @tracked selectedCourseId: string | null = null

  get selectedCourse(): Course | null {
    if (this.selectedCourseId === null) return null
    // All the courses should be in memory...
    return this.store.findInMemory("course", this.selectedCourseId)
    // return this.args.courses.find((course) => course && course.id === this.selectedCourseId) ?? null
  }

  /** Every new class starts with a lesson plan; "Just notes" is a switch in its settings. */
  @action
  addCourse() {
    if (this.isCreatingCourse === true) return
    this.selectedCourseId = null
    this.isCreatingCourse = true
    this.rpc
      .execute("COURSE_CREATE", this.args.planbook.id, false)
      .then((response) => {
        // insert the course
        this.store.insertDocument(response.data)
        this.creatingCourseId = response.data.id
        this.openCourseEditModal(response.data.id)
        later(() => {
          this.isCreatingCourse = false
          // wait for promises to resolve
          later(() => {
            this.creatingCourseId = null
          }, 250)
        }, 250)
      })
      .catch(() => {
        this.isCreatingCourse = false
      })
  }

  @action
  deleteCourse(course: Course, e: Event) {
    // Prevent also clicking the title bar (selecting the course).
    e.stopPropagation()

    let lessons = pipe(
      course.attributes.calendar.dates,
      A.filter(isCourseDateCustom),
      A.reject((cd) => cd.attributes.cardStackId === null),
    )

    this.dialog
      .blank("dialogs/confirm-delete-course", {
        lessonCount: lessons.length,
        className: "dialog--create-annotation",
      })
      .then(() => {
        this.closeCourseEditModal()
        this.rpc.execute("COURSE_DELETE", course.id)
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action selectCourse(course: Course): void {
    if (course.meta.permissions.includes("can-edit-plan")) {
      this.openCourseEditModal(course.id)
    }
  }

  @action toggleCourse(id: string, e: Event): void {
    // Prevent also clicking the title bar (reselecting the course).
    e.stopPropagation()

    if (id == this.selectedCourseId) {
      this.closeCourseEditModal()
    } else {
      this.openCourseEditModal(id)
    }
  }

  _closingModalPromise: Promise<null> | null = null

  openCourseEditModal = (id) => {
    this.selectedCourseId = id
    if (this._closingModalPromise) {
      this._closingModalPromise.then(() => this._showModal())
    } else {
      this._showModal()
    }
  }

  _showModal() {
    if (this.selectedCourse) {
      this.modalManager.openModal(
        "planner/modals/edit-course",
        {
          course: this.selectedCourse,
          findPreviousTime: this.findPreviousTime,
          planbook: this.args.planbook,
          rotationCalendar: this.args.rotationCalendar,
          showDeleteButton: true,
        },
        {
          onClose: (promise) => {
            this._closingModalPromise = promise
            this.selectedCourseId = null
          },
        },
      )
    }
  }

  closeCourseEditModal = () => {
    this.selectedCourseId = null
    this.modalManager.closeModal("planner/modals/edit-course")
  }

  /**
   * Returns the time set on a course just before the given course, or null if none exists.
   */
  @action
  findPreviousTime(course: Course): CourseCalendarTime | null {
    const index = this.args.courses.indexOf(course)
    if (index === -1) return null
    for (let i = index - 1; i >= 0; i--) {
      const previousCourse = this.args.courses[i]
      const timedDay = filterCourseDateDefault(previousCourse)
        .filter((courseDate) => isOnCourseDateDefault(previousCourse, courseDate))
        .sort((a, b) => {
          const aPosition =
            this.args.rotationCalendar.attributes.rotations.find(
              (rotation) => rotation.id === a.attributes.rotationId,
            )?.position ?? Number.MAX_SAFE_INTEGER
          const bPosition =
            this.args.rotationCalendar.attributes.rotations.find(
              (rotation) => rotation.id === b.attributes.rotationId,
            )?.position ?? Number.MAX_SAFE_INTEGER
          return aPosition - bPosition
        })
        .find((courseDate) => courseDate.attributes.time !== null)
      if (timedDay?.attributes.time) return timedDay.attributes.time
    }
    return null
  }
}
